import { Loader } from '@progress/kendo-react-indicators'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { compile } from '~/apps/ninesegsAnalyzer/routes/paths'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { exists, isAiDisabledError } from '~/utils'

import { isActiveGenerationPhase, type Phase } from '../atoms'

type RenderProgress = {
  completed: number
  total: number
}

type CommentProgress = {
  completed: number
  total: number
}

type DocumentProgress = {
  completed: number
  errors: Error[]
  total: number
}

type ProgressStepProps = {
  aiEnabled?: boolean
  brandAccountId?: string
  commentProgress?: CommentProgress
  documentProgress?: DocumentProgress
  error?: Error
  isLoading: boolean
  phase: Phase
  renderProgress?: RenderProgress
}

export const ProgressStep = ({
  aiEnabled,
  brandAccountId,
  commentProgress,
  documentProgress,
  error,
  isLoading,
  phase,
  renderProgress,
}: ProgressStepProps) => {
  const { t } = useTranslation()
  const showWarning = isActiveGenerationPhase(phase)
  // On completion, distinguish full success from partial failure by count only.
  // At completion completed === total, so successes = completed - errors.length.
  // Raw error messages are intentionally omitted (graphQLRequest errors are not
  // reliably user-readable) — count only.
  const completionFailureSummary =
    phase === 'complete' &&
    exists(documentProgress) &&
    0 < documentProgress.errors.length
      ? t('basic_output_phase_complete_with_failures', {
          failure: documentProgress.errors.length,
          success: documentProgress.completed - documentProgress.errors.length,
        })
      : undefined
  const phaseLabels: Record<Phase, string> = {
    commenting: t('basic_output_phase_commenting'),
    complete: t('basic_output_phase_complete'),
    documenting: t('basic_output_phase_documenting'),
    input: '',
    rendering: t('basic_output_phase_rendering'),
    tabulating: t('basic_output_phase_tabulating'),
  }

  return (
    <VStack align="center" spacing={4}>
      {showWarning && (
        <div
          style={{
            background: '#f3efd9',
            border: '1px solid var(--colorStateWarning)',
            borderRadius: '4px',
            fontSize: '14px',
            lineHeight: '1.5',
            maxWidth: '600px',
            padding: '12px 16px',
            textAlign: 'center',
          }}
        >
          {'⚠ '}
          {t('basic_output_do_not_close_warning')}
        </div>
      )}

      {isLoading && <Loader size="large" type="infinite-spinner" />}

      {exists(completionFailureSummary) ? (
        <div
          style={{
            background: '#f3efd9',
            border: '1px solid var(--colorStateWarning)',
            borderRadius: '4px',
            fontSize: '14px',
            lineHeight: '1.5',
            maxWidth: '600px',
            padding: '12px 16px',
            textAlign: 'center',
          }}
        >
          {completionFailureSummary}
        </div>
      ) : (
        phaseLabels[phase] && (
          <Typography
            fontSize={phase === 'complete' ? 16 : undefined}
            variant="normal"
          >
            {phaseLabels[phase]}
          </Typography>
        )
      )}

      {phase === 'rendering' && exists(renderProgress) && (
        <Typography variant="normal">
          {`${renderProgress.completed} / ${renderProgress.total}`}
        </Typography>
      )}

      {phase === 'commenting' && exists(commentProgress) && (
        <Typography variant="normal">
          {`${commentProgress.completed} / ${commentProgress.total}`}
        </Typography>
      )}

      {phase === 'documenting' && exists(documentProgress) && (
        <Typography variant="normal">
          {`${documentProgress.completed} / ${documentProgress.total}`}
        </Typography>
      )}

      {phase === 'complete' && exists(brandAccountId) && (
        <VStack align="center" spacing={2}>
          {/* documentQa はAI機能。サイドバー(useNavigationGroups.ts)と同じくaiEnabledでガードする */}
          {aiEnabled && (
            <VStack align="center" spacing={0}>
              <Link to={compile('documentQa', { brandAccountId })}>
                <Typography variant="link1">{t('document_qa')}</Typography>
              </Link>
              <Typography variant="normal">
                {t('basic_output_document_qa_description')}
              </Typography>
            </VStack>
          )}
          <VStack align="center" spacing={0}>
            <Link to={compile('documentReportList', { brandAccountId })}>
              <Typography variant="link1">{t('documents')}</Typography>
            </Link>
            <Typography variant="normal">
              {t('basic_output_document_list_description')}
            </Typography>
          </VStack>
        </VStack>
      )}

      {exists(error) && (
        <Typography color="error" variant="normal">
          {/* ClientError.message embeds the whole response JSON, so it is never
              shown; useBasicOutputGenerationFlow logs the detail to the console. */}
          {isAiDisabledError(error)
            ? t('ai_disabled_message')
            : t('an_error_has_occurred')}
        </Typography>
      )}
    </VStack>
  )
}
